import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Typography, Paper, Button, IconButton,
    Chip, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField,
    List, ListItem, ListItemText, Divider, Avatar, Grid,
    CircularProgress
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CategoryIcon from "@mui/icons-material/Category";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentsIcon from "@mui/icons-material/Payments";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import AppSearch from "../../components/base/AppSearch";
import "./Categories.css";

const Categories = () => {
    const { user } = useAuth();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // États Dialogues
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openOffersDialog, setOpenOffersDialog] = useState(false);
    const [openConsultDialog, setOpenConsultDialog] = useState(false);
    const [searchTerm, setSearchTerm] = useState(""); // ÉTAPE 1 : État pour la recherche
    // Données de travail
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [categoryTitle, setCategoryTitle] = useState("");
    const [relatedOffers, setRelatedOffers] = useState([]);
    const [currentOfferDetail, setCurrentOfferDetail] = useState(null);
    const [currentCatName, setCurrentCatName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ÉTAPE A : Déterminer le préfixe selon le rôle
    // On considère que Roles.IS_SUPERADMIN = 1 et Roles.IS_ADMIN = 2
    const rolePrefix = (user?.role_user === 1 || user?.role_user === 2)
        ? "admin"
        : "intervenant";
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    // --- Fonctions Utilitaires ---

    /**
     * Formate les dates ISO de Laravel en format lisible FR
     */
    const formatDate = (dateString) => {
        if (!dateString) return "Non renseignée";
        const date = new Date(dateString);
        return date.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // --- Fonctions API ---

    // ÉTAPE B : Mettre à jour les appels fetch
    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);
            // Utilisation du rolePrefix ici
            const response = await axios.get(`${apiUrl}/${rolePrefix}/categories/list`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data?.result) {
                setCategories(response.data.result);
            }
        } catch (err) {
            toast.error("Erreur lors du chargement des catégories");
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token, rolePrefix]);

    useEffect(() => {
        if (user) fetchCategories();
    }, [user, fetchCategories]);

    const handleSave = async () => {
        if (!categoryTitle.trim()) {
            toast.warning("Le titre est requis");
            return;
        }

        setIsSubmitting(true);
        try {
            if (selectedCategory) {
                await axios.put(
                    `${apiUrl}/${rolePrefix}/categories/update/${selectedCategory.id_category}`,
                    { titre: categoryTitle },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("Catégorie mise à jour");
            } else {
                await axios.post(
                    `${apiUrl}/${rolePrefix}/categories/add`,
                    { titre: categoryTitle },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("Catégorie créée");
            }
            setOpenDialog(false);
            setCategoryTitle("");
            fetchCategories();
        } catch (err) {
            toast.error("Vérifiez vos droits ou le titre (unique)");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!categoryToDelete) return;
        setIsSubmitting(true);
        try {
            await axios.delete(`${apiUrl}/${rolePrefix}/categories/delete/${categoryToDelete.id_category}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Catégorie supprimée avec succès");
            setOpenDeleteDialog(false);
            setCategoryToDelete(null);
            fetchCategories();
        } catch (err) {
            toast.error("Impossible de supprimer la catégorie");
        } finally {
            setIsSubmitting(false);
        }
    };


    // --- Filtrage des données ---
    // ÉTAPE 2 : On filtre la liste originale basée sur le titre
    const filteredCategories = categories.filter((cat) =>
        cat.title_category.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return (
        <Box sx={{
            display: "flex",
            minHeight: "100vh",
           // bgcolor: "#F4F7FE",
            width: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box',
            p: { xs: 2, md: 5 }
        }}>
            <Box sx={{ width: '100%' }}>

                {/* Header Section */}
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" sx={{ mb: 6 }} spacing={2}>
                    <Typography variant="h3" sx={{ fontWeight: "800", color: "#2B3674", fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                        Gestion des Catégories
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <AppSearch
                            value={searchTerm}
                            onChange={setSearchTerm}
                            placeholder="Chercher une catégorie..."
                        />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => { setSelectedCategory(null); setCategoryTitle(""); setOpenDialog(true); }}
                            sx={{
                                bgcolor: "#4318FF",
                                borderRadius: "20px", // Plus arrondi pour l'effet "pilule"
                                px: 10,               // Réduit le padding (12 était trop large)
                                py: 1.5,             // Ajuste le padding vertical au lieu d'une hauteur fixe
                                textTransform: 'none',
                                fontSize: '14px',    // Taille de police standard dashboard
                                fontWeight: 600,     // Pour plus de lisibilité
                                whiteSpace: 'nowrap', // Force le texte sur une seule ligne
                                boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)', // Optionnel: petite ombre portée
                                "&:hover": {
                                    bgcolor: "#3311CC",
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            Nouvelle Catégorie
                        </Button>
                    </Stack>
                </Stack>

                {/* Main Content */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                        <CircularProgress sx={{ color: "#4318FF" }} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {/* On utilise filteredCategories au lieu de categories */}
                        {filteredCategories.length > 0 ? (
                            filteredCategories.map((cat) => (
                                <Paper key={cat.id_category} className="category-card" elevation={0} sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <div className="icon-box">
                                            <CategoryIcon sx={{ color: "#FFF", fontSize: 20 }} />
                                        </div>
                                        <Stack direction="row">
                                            <IconButton size="small" onClick={() => { setSelectedCategory(cat); setCategoryTitle(cat.title_category); setOpenDialog(true); }}>
                                                <EditIcon sx={{ fontSize: 18, color: "white" }} />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => { setCategoryToDelete(cat); setOpenDeleteDialog(true); }}>
                                                <DeleteOutlineIcon sx={{ fontSize: 18, color: "#EE5D50" }} />
                                            </IconButton>
                                        </Stack>
                                    </Stack>

                                    <Box sx={{ mt: 2, mb: 1 }}>
                                        <Typography className="category-title-fix" sx={{ color: "white !important", fontWeight: '700', fontSize: '1.1rem' }}>
                                            {cat.title_category}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#A3AED0', mt: 0.5 }}>
                                            {cat.offres?.length || 0} offre(s) liée(s)
                                        </Typography>
                                    </Box>

                                    <Box sx={{ my: 1.5 }}>
                                        {cat.offres?.length > 0 ? (
                                            <Chip
                                                label="Opérationnelle"
                                                size="small"
                                                icon={<CheckCircleIcon style={{ color: '#05CD99', fontSize: 14 }} />}
                                                sx={{ bgcolor: "rgba(5, 205, 153, 0.1)", color: "#05CD99", fontWeight: "700", border: "1px solid #05CD99" }}
                                            />
                                        ) : (
                                            <Chip
                                                label="Vide"
                                                size="small"
                                                variant="outlined"
                                                icon={<ErrorOutlineIcon style={{ color: '#FFB547', fontSize: 14 }} />}
                                                sx={{ color: "#FFB547", borderStyle: "dashed", borderColor: '#FFB547' }}
                                            />
                                        )}
                                    </Box>

                                    {/* FOOTER DE LA CARTE */}
                                    <Box sx={{ mt: 'auto' }}>
                                        <Divider sx={{ mb: 1.5, borderColor: "rgba(255,255,255,0.1)" }} />

                                        <Stack spacing={0.5} sx={{ mb: 2 }}>
                                            {cat.user && (
                                                <Typography sx={{ color: '#A3AED0', fontSize: '10px', display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Créée par : </span>
                                                    <span style={{ color: '#FFF' }}>{cat.user.prenom_user} {cat.user.nom_user}</span>
                                                </Typography>
                                            )}
                                            <Typography sx={{ color: '#A3AED0', fontSize: '10px', display: 'flex', justifyContent: 'space-between' }}>
                                                <span>Créé le :</span>
                                                <span style={{ color: '#FFF' }}>{formatDate(cat.created_at)}</span>
                                            </Typography>
                                            <Typography sx={{ color: '#A3AED0', fontSize: '10px', display: 'flex', justifyContent: 'space-between' }}>
                                                <span>Modifié le :</span>
                                                <span style={{ color: '#FFF' }}>{formatDate(cat.updated_at)}</span>
                                            </Typography>
                                        </Stack>

                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography sx={{ color: '#A3AED0', fontSize: '11px', fontWeight: 'bold' }}>
                                                ID: #{cat.id_category}
                                            </Typography>
                                            <Typography
                                                onClick={() => { setRelatedOffers(cat.offres || []); setCurrentCatName(cat.title_category); setOpenOffersDialog(true); }}
                                                sx={{
                                                    color: "#70adf5",
                                                    fontWeight: "700",
                                                    fontSize: 12,
                                                    cursor: 'pointer',
                                                    "&:hover": { color: "white", textDecoration: 'underline' }
                                                }}
                                            >
                                                Voir détails →
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </Paper>
                            ))
                        ) : (
                            /* Message si la recherche ne donne rien */
                            <Box sx={{ width: '100%', textAlign: 'center', py: 5 }}>
                                <Typography sx={{ color: '#A3AED0', fontSize: '1.1rem' }}>
                                    Aucune catégorie trouvée pour "{searchTerm}"
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* --- DIALOGUES (Inchangés mais inclus pour le code complet) --- */}

            <Dialog open={openOffersDialog} onClose={() => setOpenOffersDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: '800', color: '#2B3674' }}>Offres dans "{currentCatName}"</DialogTitle>
                <DialogContent>
                    {relatedOffers.length > 0 ? (
                        <List>
                            {relatedOffers.map((off) => (
                                <ListItem
                                    key={off.id_offre}
                                    secondaryAction={
                                        <IconButton edge="end" onClick={() => { setCurrentOfferDetail(off); setOpenConsultDialog(true); }}>
                                            <VisibilityIcon sx={{ color: '#4318FF' }} />
                                        </IconButton>
                                    }
                                    sx={{ bgcolor: '#F4F7FE', borderRadius: '12px', mb: 1 }}
                                >
                                    <ListItemText
                                        primary={off.info_offre?.titre_offre}
                                        secondary={`Par: ${off.intervenant?.prenom_user} ${off.intervenant?.nom_user}`}
                                        primaryTypographyProps={{ fontWeight: '700', color: '#2B3674' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography sx={{ textAlign: 'center', py: 4, color: '#A3AED0' }}>Aucune offre liée à cette catégorie.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenOffersDialog(false)} sx={{ color: '#A3AED0' }}>Fermer</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openConsultDialog} onClose={() => setOpenConsultDialog(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '24px' } }}>
                {currentOfferDetail && (
                    <>
                        <DialogTitle sx={{ bgcolor: '#4318FF', color: 'white', fontWeight: '800' }}>
                            Détails de l'offre
                        </DialogTitle>
                        <DialogContent sx={{ mt: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="overline" sx={{ color: '#A3AED0', fontWeight: '700' }}>Informations Financières</Typography>
                                    <Stack spacing={2} sx={{ mt: 1 }}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <PaymentsIcon color="primary" />
                                            <Box>
                                                <Typography variant="body2" color="textSecondary">Prix</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: '800' }}>{currentOfferDetail.info_offre?.prix_offre} €</Typography>
                                            </Box>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <AccessTimeIcon color="primary" />
                                            <Box>
                                                <Typography variant="body2" color="textSecondary">Durée</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: '800' }}>{currentOfferDetail.info_offre?.duree_offre} min</Typography>
                                            </Box>
                                        </Paper>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="overline" sx={{ color: '#A3AED0', fontWeight: '700' }}>Intervenant</Typography>
                                    <Paper sx={{ p: 2, mt: 1, bgcolor: '#F4F7FE', borderRadius: '12px' }} elevation={0}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar src={currentOfferDetail.intervenant?.photo_user} sx={{ width: 56, height: 56, border: '2px solid #4318FF' }} />
                                            <Box>
                                                <Typography sx={{ fontWeight: '700', color: '#2B3674' }}>
                                                    {currentOfferDetail.intervenant?.prenom_user} {currentOfferDetail.intervenant?.nom_user}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">{currentOfferDetail.intervenant?.email_user}</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="overline" sx={{ color: '#A3AED0', fontWeight: '700' }}>Description de l'offre</Typography>
                                    <Typography sx={{ mt: 1, p: 2, bgcolor: '#F8F9FA', borderRadius: '12px', color: '#2B3674', lineHeight: 1.6 }}>
                                        {currentOfferDetail.info_offre?.description_offre || "Aucune description fournie."}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ p: 3 }}>
                            <Button onClick={() => setOpenConsultDialog(false)} variant="contained" sx={{ bgcolor: '#4318FF', borderRadius: '12px', px: 4 }}>Fermer</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ color: '#EE5D50', display: 'flex', alignItems: 'center', gap: 1, fontWeight: '800' }}>
                    <WarningAmberIcon /> Confirmation
                </DialogTitle>
                <DialogContent>
                    <Typography>Voulez-vous vraiment supprimer la catégorie <b>{categoryToDelete?.title_category}</b> ? Cette action est irréversible.</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#A3AED0' }}>Annuler</Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{ bgcolor: "#EE5D50", borderRadius: '12px', "&:hover": { bgcolor: "#D32F2F" } }}
                    >
                        {isSubmitting ? "Suppression..." : "Supprimer"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ fontWeight: '800', color: '#2B3674' }}>
                    {selectedCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Titre de la catégorie"
                        fullWidth
                        variant="outlined"
                        value={categoryTitle}
                        onChange={(e) => setCategoryTitle(e.target.value)}
                        sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: '12px' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDialog(false)} sx={{ color: '#A3AED0' }}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSubmitting}
                        sx={{ bgcolor: "#4318FF", borderRadius: '12px', px: 3 }}
                    >
                        {isSubmitting ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default Categories;